import { AuthURL, BASE_URL } from "../screens/Dashboard/CommonApi";
import {
  clearOrganizationSession,
  getActiveOrganizationHeaderValue,
  selectDefaultOrganization,
  setCurrentUser,
  setOrganizations,
} from "./organizationContext";

let refreshPromise = null;

const isAuthRefreshRequest = (input) => {
  const url = typeof input === "string" ? input : input?.url || "";
  return url.includes("/refreshToken");
};

const getRequestUrl = (input) => (typeof input === "string" ? input : input?.url || "");

const isApiRequest = (input) => {
  const url = getRequestUrl(input);
  return url.startsWith(BASE_URL) || (url.startsWith("/") && !url.startsWith("/auth"));
};

const hasBearerHeader = (init = {}) => {
  const headers = new Headers(init.headers || {});
  return headers.has("Authorization");
};

const withTenantContext = (init = {}) => {
  const token = localStorage.getItem("jwtToken");
  const organizationId = getActiveOrganizationHeaderValue();
  const headers = new Headers(init.headers || {});

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  } else if (token && headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  if (organizationId && !headers.has("X-Organization-Id")) {
    headers.set("X-Organization-Id", organizationId);
  }

  const nextInit = {
    ...init,
    headers,
  };

  if (organizationId && nextInit.body) {
    nextInit.body = withActiveOrganizationBody(nextInit.body, organizationId, headers);
  }

  return nextInit;
};

const withActiveOrganizationBody = (body, organizationId, headers) => {
  if (typeof body === "string") {
    const contentType = headers.get("Content-Type") || "";
    if (!contentType.includes("application/json")) return body;
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return JSON.stringify({ ...parsed, uniqueId: organizationId });
      }
    } catch (error) {
      return body;
    }
  }

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    body.set("uniqueId", organizationId);
  }

  return body;
};

const withLatestToken = (init = {}) => {
  const token = localStorage.getItem("jwtToken");
  if (!token) return init;

  const headers = new Headers(init.headers || {});
  if (headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return {
    ...init,
    headers,
  };
};

const clearSession = () => {
  ["uniqueId1", "jwtToken", "userId", "refreshToken", "loginDate"].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  clearOrganizationSession();
};

export const refreshAuthToken = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  if (!refreshPromise) {
    refreshPromise = window
      .fetch(`${AuthURL}/refreshToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      })
      .then(async (response) => {
        if (!response.ok) {
          clearSession();
          return null;
        }

        const data = await response.json();
        if (data.jwtToken) localStorage.setItem("jwtToken", data.jwtToken);
        if (data.refreshToken) localStorage.setItem("refreshToken", data.refreshToken);
        if (data.user) setCurrentUser(data.user);
        if (Array.isArray(data.organizations)) {
          setOrganizations(data.organizations);
          selectDefaultOrganization(data.organizations);
        }
        return data.jwtToken || null;
      })
      .catch((error) => {
        console.error("Unable to refresh auth token:", error);
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

export const installAuthFetchInterceptor = () => {
  if (typeof window === "undefined" || window.__zarpAuthFetchInstalled) return;

  const originalFetch = window.fetch.bind(window);
  window.__zarpOriginalFetch = originalFetch;
  window.__zarpAuthFetchInstalled = true;

  window.fetch = async (input, init = {}) => {
    const shouldAttachTenantContext = isApiRequest(input) && !isAuthRefreshRequest(input);
    const shouldAttachLatestToken = shouldAttachTenantContext || hasBearerHeader(init);
    const requestInit = shouldAttachTenantContext
      ? withTenantContext(init)
      : shouldAttachLatestToken
        ? withLatestToken(init)
        : init;
    const response = await originalFetch(input, requestInit);

    if (
      response.status !== 401 &&
      response.status !== 403
    ) {
      return response;
    }

    if (isAuthRefreshRequest(input) || !shouldAttachLatestToken) {
      return response;
    }

    const nextToken = await refreshAuthToken();
    if (!nextToken) return response;

    return originalFetch(input, shouldAttachTenantContext ? withTenantContext(init) : withLatestToken(init));
  };
};

export const startAuthRefreshLoop = () => {
  if (typeof window === "undefined" || window.__zarpAuthRefreshTimer) return;

  window.__zarpAuthRefreshTimer = window.setInterval(() => {
    if (localStorage.getItem("jwtToken") && localStorage.getItem("refreshToken")) {
      refreshAuthToken();
    }
  }, 2 * 60 * 1000);
};
